if (!customElements.get('map-hotspots-google')) {
  customElements.define(
    'map-hotspots-google',
    class MapHotspotsGoogle extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        const BREAKPOINT = 768

        class Marker {
          constructor(index, marker, iconUrl, activeIconUrl, iconSize) {
            this.index = index
            this.marker = marker
            this.iconUrl = iconUrl
            this.activeIconUrl = activeIconUrl
            this.iconSize = iconSize
            this.isActive = false

            this.updateMarkerIcon()
          }

          getIconUrl() {
            return this.isActive ? this.activeIconUrl : this.iconUrl
          }

          setActive(isActive) {
            if (this.isActive !== isActive) {
              this.isActive = isActive

              this.updateMarkerIcon()
            }
          }

          setIconSize(iconSize) {
            if (this.iconSize !== iconSize) {
              this.iconSize = iconSize

              this.updateMarkerIcon()
            }
          }

          updateMarkerIcon() {
            this.marker.setIcon({
              url: this.getIconUrl(),
              scaledSize: this.iconSize
            })
          }
        }

        class GoogleMapHotspots {
          constructor(el) {
            const { apiKey, mapCenterMobile, mapCenterDesktop, mapZoomMobile,mapZoomDesktop,
              hotspotImage, hotspotImageAspectRatio, hotspotActiveImage, mapStyles, hotspots } = el.dataset

            this.apiKey = apiKey
            this.mapCenterMobile = this.getLocationFromCoordinates(mapCenterMobile)
            this.mapCenterDesktop = this.getLocationFromCoordinates(mapCenterDesktop)
            this.mapZoomMobile = parseFloat(mapZoomMobile)
            this.mapZoomDesktop = parseFloat(mapZoomDesktop)
            this.hotspotImage = hotspotImage
            this.hotspotImageAspectRatio = hotspotImageAspectRatio
            this.hotspotActiveImage = hotspotActiveImage
            this.mapStyles = this.parseJSON(mapStyles)
            this.hotspots = this.parseJSON(hotspots, []).map(({ position, title }) => ({
              position: this.getLocationFromCoordinates(position),
              title
            }))
            this.markers = []
            this.infoWindows = []

            this.el = el
            this.mapEl = el.querySelector('.js-map-hotspots-google-map')
            this.inner = el.querySelector('.js-map-hotspots-google-inner')
            this.carousel = el.querySelector('.js-map-hotspots-google-carousel')
            this.swiperIns = null
            this.isClickingButton = false
          }

          getLocationFromCoordinates(coordinates) {
            const [lat, lng] = coordinates.split(',').map(number => parseFloat(number))

            return { lat, lng }
          }

          parseJSON(str, fallback = {}) {
            try {
              return JSON.parse(str)
            } catch (_e) {
              return fallback
            }
          }

          watchScreenSize(callback = null) {
            const setIsMobile = () => {
              const oldIsMobile = this.isMobile
              this.isMobile = window.innerWidth < BREAKPOINT

              if (callback && oldIsMobile !== this.isMobile) {
                callback()
              }
            }

            window.addEventListener('resize', setIsMobile)
            setIsMobile()
          }

          async init() {
            this.api = await this.getGoogleMapAPI()

            this.watchScreenSize(() => {
              this.setMarkerSize()
              this.initMap()
              this.initHotspots()
            })

            this.initCarousel()
          }

          getGoogleMapAPI() {
            return new Promise((resolve) => {
              if (window.google?.maps) {
                resolve(window.google.maps)
              } else {
                window.__onGoogleMapLoaded = () => {
                  resolve(window.google.maps)
                }

                const script = document.createElement('script')
                script.defer = true
                script.async = true
                script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&callback=__onGoogleMapLoaded`
                document.head.appendChild(script)
              }
            })
          }

          setMarkerSize() {
            const markerWidth = this.isMobile ? 30 : 48

            this.markerSize = new this.api.Size(markerWidth, Math.round(markerWidth / this.hotspotImageAspectRatio))
          }

          initMap() {
            const options = {
              zoom: 1,
              disableDefaultUI: true,
              styles: this.mapStyles
            }

            if (!this.map) {
              this.map = new this.api.Map(this.mapEl, options)

              this.map.addListener('click', () => {
                this.inner.classList.remove('is-active')
                this.updateMarkers(null, false)
              })
            }

            if (this.isMobile) {
              this.map.setOptions({
                ...options,
                center: this.mapCenterMobile,
                zoom: this.mapZoomMobile
              })
            } else {
              this.map.setOptions({
                ...options,
                center: this.mapCenterDesktop,
                zoom: this.mapZoomDesktop
              })
            }

            this.overlay = new this.api.OverlayView()
            this.overlay.draw = () => {}
            this.overlay.setMap(this.map)
          }

          initHotspots() {
            this.hotspots.forEach((hotspot, index) => {
              if (!this.infoWindows[index]) {
                const cardEl = this.el.querySelector(`.js-map-hotspots-card[data-index="${index}"]`)

                this.infoWindows[index] = new this.api.InfoWindow({
                  disableAutoPan: true,
                  content: `<div class="map-hotspots-card">${cardEl.innerHTML}</div>`
                })
              }

              if (!this.markers[index]) {
                const marker = new this.api.Marker({
                  ...hotspot,
                  map: this.map
                })

                this.markers[index] = new Marker(index, marker, this.hotspotImage, this.hotspotActiveImage, this.markerSize)

                marker.addListener('click', () => {
                  this.isClickingButton = true
                  this.handleMarkerClick(this.markers[index])
                })
              }

              this.markers[index].setIconSize(this.markerSize)
            })
          }

          handleMarkerClick(marker, updateCarousel = true) {
            const isActive = !marker.isActive

            this.updateMarkers(marker, isActive)
            this.inner.classList.toggle('is-active', isActive)

            if (updateCarousel) {
              this.swiperIns.slideTo(marker.index)
            }
          }

          updateMarkers(marker, isActive) {
            if (marker && isActive) {
              marker.setActive(true)

              this.infoWindows[marker.index].open({
                map: this.map,
                anchor: marker.marker,
                shouldFocus: true
              })

              this.infoWindows[marker.index].addListener('closeclick', () => {
                this.updateMarkers(null, false)
              })

              setTimeout(() => {
                this.panToMarker(marker.marker)
              }, 200)
            }

            this.markers.forEach((item) => {
              if (!isActive || item !== marker) {
                item.setActive(false)
                this.infoWindows[item.index].close()
              }
            })
          }

          panToMarker(marker) {
            if (!marker) {
              return
            }

            const mapEl = this.map.getDiv()
            const mapHeight = mapEl.offsetHeight
            const bottomPadding = 15
            let infoWindowHeight

            if (this.isMobile) {
              infoWindowHeight = this.carousel.offsetHeight
            } else {
              const infoWindowEl = mapEl.querySelector('.gm-style-iw')
              infoWindowHeight = infoWindowEl.offsetHeight
            }

            let offsetTop = 0

            if (infoWindowHeight + bottomPadding > mapHeight / 2) {
              offsetTop = infoWindowHeight + bottomPadding - mapHeight / 2
            }

            const { x, y } = this.overlay.projection.fromLatLngToContainerPixel(marker.getPosition())
            const center = this.overlay.projection.fromContainerPixelToLatLng({ x, y: y + offsetTop })
            this.map.panTo(center)
          }

          initCarousel() {
            this.swiperIns = new Swiper(this.carousel, {
              cssMode: false,
              slidesPerView: 'auto',
              centeredSlides: true,
              speed: 600,
              pagination: {
                el: this.carousel.querySelector('.swiper-pagination'),
                clickable: true
              }
            })

            this.swiperIns.on('slideChange', () => {
              if (!this.isClickingButton) {
                const marker = this.markers[this.swiperIns.realIndex]

                if (marker) {
                  this.handleMarkerClick(marker, false)
                }
              }

              this.isClickingButton = false
            })
          }
        }

        new GoogleMapHotspots(this).init()
      }
    }
  );
}